import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Plus, RefreshCw, FileEdit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AppHeader } from '@/components/layout/AppHeader';
import { useComposerSession } from '@/hooks/useComposerSession';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { ComposerLayout } from '@/components/composer/ComposerLayout';
import { Card, CardContent } from '@/components/ui/card';

function DesktopWall() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center">
      <span className="logo-text text-[32px] text-foreground tracking-tight mb-6">
        Veracord<span style={{ fontSize: '9px', fontWeight: 400, position: 'relative', top: '-10px', marginLeft: '1px' }}>™</span>
      </span>
      <h1 className="text-xl font-semibold text-foreground mb-2">Policy Builder is optimized for desktop</h1>
      <p className="text-sm text-muted-foreground mb-6 max-w-sm">
        Please open Policy Builder on a laptop or desktop browser for the best experience.
      </p>
      <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
    </div>
  );
}

/**
 * Ensures the ?new= param is always a UUID. If it arrives as the legacy
 * literal 'true', promotes it to a UUID in-place via history.replaceState
 * so the URL itself is the stable idempotency-key store across StrictMode
 * remounts and Realtime refreshes.
 */
function resolveIdempotencyKey(): string | null {
  const params = new URLSearchParams(window.location.search);
  const value = params.get('new');
  if (!value) return null;
  if (value !== 'true') return value;           // already a UUID
  const uuid = crypto.randomUUID();
  params.set('new', uuid);
  history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  return uuid;
}

function ComposerSelector() {
  const navigate = useNavigate();

  const selectorCards = [
    {
      icon: Plus,
      title: 'Create Policy',
      description:
        'Start a new policy from scratch. Use Vera to draft content grounded in your regulatory library, or upload an existing document to get started.',
      buttonLabel: '+ Create Policy',
      onClick: () => {
        const key = crypto.randomUUID();
        navigate(`/composer?new=${key}`);
      },
    },
    {
      icon: RefreshCw,
      title: 'Update a Policy',
      description:
        'Open an approved policy to create a new version. The existing record stays locked — Policy Builder creates a new draft for review and approval.',
      buttonLabel: 'Select Policy',
      onClick: () => navigate('/policies?status=Approved&composerMode=update'),
    },
    {
      icon: FileEdit,
      title: 'Continue a Draft',
      description:
        'Pick up where you left off on an in-progress draft. No approvals have been submitted yet — your work is still editable.',
      buttonLabel: 'Open Draft',
      onClick: () => navigate('/policies?status=draft&composerMode=draft'),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="container px-4 py-12 max-w-5xl mx-auto">
        <div className="mb-10 text-center">
          <div className="flex items-center justify-center gap-2.5 mb-2">
            <h1 className="text-2xl md:text-[32px] font-extrabold tracking-tight">
              Policy Builder
            </h1>
            <Badge
              variant="outline"
              className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground border-muted-foreground/30 mt-1"
            >
              Beta
            </Badge>
          </div>
          <p className="text-base font-medium text-foreground/90 text-balance">
            Designed to help you prepare policy updates faster.
          </p>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto mt-1 text-balance">
            Final review and approval are always required before a policy becomes
            part of your Veracord record.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {selectorCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card
                key={card.title}
                className="border-l-4 border-l-primary bg-card shadow-sm hover:shadow-md transition-shadow"
              >
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="rounded-md bg-primary/10 p-2">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="text-lg font-semibold">{card.title}</h2>
                  </div>
                  <p className="text-sm text-muted-foreground mb-6 flex-1">
                    {card.description}
                  </p>
                  <Button
                    className="w-full"
                    onClick={card.onClick}
                  >
                    {card.buttonLabel}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}

function ComposerWorkspace({
  urlPolicyId,
  isNew,
  mode,
  idempotencyKey,
}: {
  urlPolicyId: string | null;
  isNew: boolean;
  mode: string | null;
  idempotencyKey: string | null;
}) {
  const session = useComposerSession(urlPolicyId, isNew, mode, idempotencyKey);

  if (session.isLoading) {
    return (
      <>
        <AppHeader />
        <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </>
    );
  }

  if (session.error) {
    return (
      <>
        <AppHeader />
        <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-background">
          <p className="text-destructive text-sm">{session.error}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <AppHeader />
      <ComposerLayout
        sessionId={session.sessionId}
        draftId={session.draftId}
        policyId={session.policyId}
        sessionName={session.sessionName}
        initialHtml={session.initialHtml}
        initialJson={session.initialJson}
        originalFileUrl={session.originalFileUrl}
        updateSessionName={session.updateSessionName}
        saveDraft={session.saveDraft}
      />
    </>
  );
}

export default function Composer() {
  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 1024);
  const [searchParams] = useSearchParams();
  const urlPolicyId = searchParams.get('policy_id');
  const idempotencyKey = resolveIdempotencyKey();
  const isNew = !!idempotencyKey;
  const mode = searchParams.get('mode');
  const { data: subscriptionStatus } = useSubscriptionStatus();

  useEffect(() => {
    const handler = () => setIsNarrow(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  if (isNarrow) return <DesktopWall />;

  // Soft-Close: Composer is a paid-only surface. Soft-closed clinics see a
  // blocked wall — the banner already shows the Reactivate CTA.
  if (subscriptionStatus?.status === 'soft_closed') {
    return (
      <>
        <AppHeader />
        <div className="flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center bg-background px-6 text-center gap-4">
          <p className="text-base font-semibold text-foreground">Policy Builder is not available</p>
          <p className="text-sm text-muted-foreground max-w-sm">
            Your subscription has ended. Reactivate to create and edit policies.
          </p>
        </div>
      </>
    );
  }

  // Gate: no params → show selector instead of initializing a session
  if (!urlPolicyId && !isNew) {
    return <ComposerSelector />;
  }

  return <ComposerWorkspace urlPolicyId={urlPolicyId} isNew={isNew} mode={mode} idempotencyKey={idempotencyKey} />;
}
